/**
 * Session failures, branchable.
 *
 * Every error out of this package is a `WhipError` carrying a stable `reason`, so a caller can tell a stream it built wrong
 * from an endpoint that rejected it from a gateway with no capacity — without matching on message strings, which change.
 */
export type WhipErrorReason =
	/** The options describe a session that cannot be attempted: bad endpoint, or a stream without exactly one audio and one video track. */
	| 'invalid-options'
	/** `createOffer`/`setLocalDescription` failed locally. */
	| 'offer-failed'
	/** 400 — the gateway or transcoder would not parse the offer. */
	| 'bad-request'
	/** 401 — missing identity headers on the production route, or a malformed `x-auth-stream-keys`. */
	| 'unauthorized'
	/** 415 — the body was not sent as `application/sdp`. */
	| 'unsupported-content-type'
	/** 422 — well-formed, but the server declined to act on it. */
	| 'unprocessable'
	/** 503 — no transcoder pod available, or every region exhausted. Carries `retryAt` when the server sent a `Retry-After`. */
	| 'unavailable'
	/** 502 — the gateway could not reach a transcoder, or got a protocol error from one. */
	| 'upstream-failed'
	/** Any other non-201 response. */
	| 'http-error'
	/** `fetch` itself rejected: DNS, TLS, CORS, or the network. */
	| 'network-error'
	/** The answer arrived but `setRemoteDescription` rejected it. */
	| 'answer-failed'
	/** `start()` on a session that has already been started. A session is one-shot. */
	| 'already-started'
	/** A verb after `close()`. A session is one-shot; create another. */
	| 'closed';

export class WhipError extends Error {
	readonly reason: WhipErrorReason;
	/** The HTTP status, when a response is what produced this error. */
	readonly status?: number;
	/**
	 * When the server said it would be worth trying again, from `Retry-After`. Only ever set on `unavailable`.
	 *
	 * An instant rather than a duration: both forms the header allows are resolved at parse time, and a delay stays
	 * correct however long this error sits in a queue before anyone acts on it.
	 */
	readonly retryAt?: Date;

	constructor(reason: WhipErrorReason, message: string, options?: { cause?: unknown; status?: number; retryAt?: Date }) {
		super(message, options);
		this.name = 'WhipError';
		this.reason = reason;
		this.status = options?.status;
		this.retryAt = options?.retryAt;
	}
}

/** Whatever the platform threw, as a string worth putting in a message. */
export function errorMessage(error: unknown): string {
	return error instanceof Error ? error.message : String(error);
}
